/**
 * ProjectContainer — the client half of a project container.
 *
 * A container is one cloud project (server.js /api/projects; docs/PROJECT_CONTAINER.md):
 * its setup, its basis of design, its memory (an append-only event ledger), its
 * documents, models and deliverables. Supabase is the working copy; the ledger is
 * the fold of the events and is read, never written, from here. The "current"
 * container is a per-viewer convenience in localStorage; the Navigator and the
 * Info dock follow it through PROJECT_EVENT.
 */

import { SupabaseAuthProvider, type AuthState } from '../services/auth/SupabaseAuthProvider.js';
import { loadSetup, type ProjectSetup } from './ProjectSetup.js';
import eventRegistry from '../../scripts/ifc_worker/pipeline/bim/data/project_events.json';
import designBuildPattern from '../../scripts/ifc_worker/pipeline/bim/data/design_build_pattern.json';

export type ProjectStatus = 'draft' | 'active' | 'published' | 'archived';
export type BodStatus = 'none' | 'captured' | 'extracted' | 'confirmed';
export type DocumentKind = 'scheme' | 'bod' | 'drawing-set' | 'package' | 'reference' | 'model';
export type EventSource = 'ui' | 'worker' | 'agent' | 'node';

export const EVENT_KINDS = Object.keys(eventRegistry.kinds);
export const DOCUMENT_KINDS = eventRegistry.document_kinds as DocumentKind[];

export interface ProgramSpace { name: string; area?: number | null; count?: number | null }

/** The owner's intent (pipeline/bim/data/basis_of_design.schema.json, R3). */
export interface BasisOfDesign {
    project_name: string;
    intent: string;
    community_type?: string | null;
    occupancy_group?: string | null;
    construction_type?: string | null;
    program?: { spaces?: ProgramSpace[]; template?: string | null };
    performance?: { targets: string[] };
    budget_tier?: 'modest' | 'standard' | 'enhanced' | null;
    site?: { facility_id?: string | null; parcel_id?: string | null; address?: string | null };
    phases_requested?: string[];
    systems_requested?: string[];
    references?: { document_id: string; pages?: number[] }[];
    domain?: 'building' | 'landscape' | 'both';
    captured_by?: string | null;
    captured_at?: string | null;
    confirmed_by?: string | null;
    confirmed_at?: string | null;
}

export interface Ledger {
    project_id?: string | null;
    name?: string | null;
    status?: ProjectStatus;
    phase?: string;
    room?: string[];
    lod_current?: number;
    bod_status?: BodStatus;
    basis_of_design?: BasisOfDesign | null;
    setup?: Record<string, unknown> | null;
    documents?: Record<string, { count: number; latest: Record<string, unknown> | null }>;
    models?: Record<string, Record<string, unknown>>;
    /** The zone briefs saved per model (narrative.saved): the Operate-phase declaration. */
    narratives?: Record<string, { zone_count?: number; version?: number; saved_at?: string }>;
    /** The DD steps run per model version: supplier, part_marks, constructability summaries. */
    dd?: Record<string, { supplier?: Record<string, unknown>; part_marks?: Record<string, unknown>; constructability?: Record<string, unknown> }>;
    deliverables?: Record<string, unknown>[];
    gates_passed?: string[];
    agent_runs?: number;
    published?: Record<string, unknown> | null;
    last_seq?: number;
    updated_at?: string | null;
    warnings?: { seq: number; kind: string; message: string }[];
    [key: string]: unknown;
}

export interface ProjectRecord {
    id: string;
    name: string;
    status: ProjectStatus;
    communityType: string | null;
    facilityId: string | null;
    mokuId: string | null;
    gcsRoot: string | null;
    nodeProjectId: string | null;
    publishedAt: string | null;
    createdAt: string;
    updatedAt: string;
    setup?: Record<string, unknown>;
    basisOfDesign?: Partial<BasisOfDesign>;
    ledger: Ledger;
    cue: Record<string, unknown> | null;
}

export interface ProjectEvent { seq: number; kind: string; payload: Record<string, unknown>; actor_user_id?: string | null; source: EventSource; at: string }

export interface LinkableModel { id: string; name: string; lodPhase: string; facilityType?: string | null; uploadedAt: string; projectId: string | null; linked: boolean }

/** A contract tier (contract_tiers.json) with its state on this container. */
export interface ContractTier {
    id: string; title: string; phase_exit: string | null; gate: string | null; status: string;
    sources: { kind: string; ref: string; note?: string; requires?: string[] }[];
    built: { document_id: string; ids_id: string; spec_count: number; sha256?: string; at: string; registered: boolean; contract_id?: string | null } | null;
    gate_verdict: { passed: boolean; ids?: string; at?: string; model_id?: string; failures?: unknown[] } | null;
}
export interface RegisteredContract { contract_id: string; contract_type: string; status: string; ids_id?: string | null; gcs_path?: string | null; ids_xml_hash?: string | null; published_at?: string | null; specification_count?: number | null; stub?: boolean }
export interface ContractsView { tiers: ContractTier[]; documents: ProjectDocument[]; registered: RegisteredContract[] }
export interface ContractCheck { documentId: string; tier: string; passedAll: boolean; passed: number; failed: number; total: number; failures: { spec: string; failing_entities?: unknown }[]; ledger: Ledger }
export interface IssuedSheet { number: string; title: string; status: string; file?: string; pdf_url?: string | null; svg_url?: string | null; blocked_by?: unknown }
export interface IssuedSet { success: boolean; document: ProjectDocument; issue: number; sheets: IssuedSheet[]; counts: Record<string, number>; gate: 'pass' | 'partial'; ledger: Ledger }

/** One proposed basis value from a scheme document (basis_of_design.schema.json `candidate`). */
export interface Candidate { field: string; value: unknown; page?: number | null; quote?: string | null; confidence?: 'high' | 'medium' | 'low' | null }

/** A supplier record (pipeline/bim/data/suppliers.json) with the constructability defaults its capacities imply. */
export interface DdSupplier { id: string; name: string; provisional: boolean; source?: string; address?: Record<string, string>; capacities?: Record<string, unknown>; constructability_defaults?: Record<string, unknown> }
export type DdStep = 'resolve-supplier' | 'part-marks' | 'constructability';
export interface QuantitiesIssue { success: boolean; document: ProjectDocument; issue: number; report: Record<string, any>; ledger: Ledger }
export interface DdResult { success: boolean; step: DdStep; report: Record<string, any>; model: LinkableModel; ledger: Ledger }

export interface ProjectDocument {
    id: string; project_id: string; kind: DocumentKind; title: string; bucket: string; gcs_path: string; mime_type?: string | null;
    file_size?: number | null; sha256?: string | null; status: 'pending' | 'uploaded' | 'processed' | 'superseded'; pages?: number | null; added_at: string;
}

export class ProjectApiError extends Error {
    constructor(message: string, public code: string, public status: number) { super(message); }
    get migrationPending(): boolean { return this.code === 'MIGRATION_PENDING'; }
    get unauthorized(): boolean { return this.status === 401; }
}

const CURRENT_KEY = 'mokulab.project.current.v1';
export const PROJECT_EVENT = 'ws-project';

export function currentProjectId(): string | null {
    try { return localStorage.getItem(CURRENT_KEY); } catch { return null; }
}

export function setCurrentProjectId(id: string | null): void {
    try { if (id) localStorage.setItem(CURRENT_KEY, id); else localStorage.removeItem(CURRENT_KEY); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent(PROJECT_EVENT, { detail: { projectId: id } }));
}

/** Thin client over /api/projects. Every call carries the viewer's Supabase JWT. */
export class ProjectApi {
    private auth = SupabaseAuthProvider.getInstance();

    isSignedIn(): boolean { return this.auth.isAuthenticated(); }
    userEmail(): string | null { return this.auth.getCurrentUser()?.email ?? null; }
    userId(): string | null { return this.auth.getCurrentUser()?.id ?? null; }
    onAuthChange(cb: (state: AuthState) => void): () => void { return this.auth.onAuthStateChange(cb); }

    private async headers(json = true): Promise<Record<string, string>> {
        const token = await this.auth.getToken();
        if (!token) throw new ProjectApiError('Sign in to work on a project container', 'UNAUTHORIZED', 401);
        const h: Record<string, string> = { 'Authorization': `Bearer ${token}` };
        if (json) h['Content-Type'] = 'application/json';
        return h;
    }

    async call<T>(path: string, init: RequestInit = {}): Promise<T> {
        const res = await fetch(path, { ...init, headers: { ...(await this.headers(init.body != null)), ...(init.headers as Record<string, string> | undefined) } });
        const text = await res.text();
        let body: any = {};
        try { body = text ? JSON.parse(text) : {}; } catch { body = { error: { message: text.slice(0, 200) } }; }
        if (!res.ok) throw new ProjectApiError(body?.error?.message || `${res.status} ${res.statusText}`, body?.error?.code || 'HTTP_ERROR', res.status);
        return body as T;
    }

    async list(): Promise<ProjectRecord[]> {
        return (await this.call<{ projects: ProjectRecord[] }>('/api/projects')).projects;
    }

    async get(id: string): Promise<ProjectRecord> {
        return (await this.call<{ project: ProjectRecord }>(`/api/projects/${encodeURIComponent(id)}`)).project;
    }

    async create(body: { name: string; communityType?: string | null; facilityId?: string | null; mokuId?: string | null; setup?: unknown; basisOfDesign?: BasisOfDesign }): Promise<ProjectRecord> {
        return (await this.call<{ project: ProjectRecord }>('/api/projects', { method: 'POST', body: JSON.stringify(body) })).project;
    }

    async patch(id: string, body: { name?: string; status?: 'active' | 'archived'; setup?: unknown }): Promise<ProjectRecord> {
        return (await this.call<{ project: ProjectRecord }>(`/api/projects/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) })).project;
    }

    async appendEvent(id: string, kind: string, payload: Record<string, unknown>, source: EventSource = 'ui'): Promise<{ event: ProjectEvent; ledger: Ledger }> {
        return this.call(`/api/projects/${encodeURIComponent(id)}/events`, { method: 'POST', body: JSON.stringify({ kind, payload, source }) });
    }

    async events(id: string, after = 0, limit = 200): Promise<ProjectEvent[]> {
        return (await this.call<{ events: ProjectEvent[] }>(`/api/projects/${encodeURIComponent(id)}/events?after=${after}&limit=${limit}`)).events;
    }

    async documents(id: string): Promise<ProjectDocument[]> {
        return (await this.call<{ documents: ProjectDocument[] }>(`/api/projects/${encodeURIComponent(id)}/documents`)).documents;
    }

    async cue(id: string, fresh = false): Promise<{ ledger: Ledger; cue: Record<string, unknown> | null }> {
        return this.call(`/api/projects/${encodeURIComponent(id)}/cue${fresh ? '?fresh=1' : ''}`);
    }

    /** Three steps: start (signed URL), PUT the bytes, complete (records document.added). */
    async uploadDocument(id: string, file: File, kind: DocumentKind, title?: string, onStage?: (stage: 'start' | 'upload' | 'complete') => void): Promise<{ document: ProjectDocument; ledger: Ledger }> {
        onStage?.('start');
        const started = await this.call<{ document: ProjectDocument; uploadUrl: string; contentType: string }>(
            `/api/projects/${encodeURIComponent(id)}/documents`,
            { method: 'POST', body: JSON.stringify({ filename: file.name, kind, title: title || file.name, contentType: file.type || undefined, fileSize: file.size }) },
        );
        onStage?.('upload');
        const put = await fetch(started.uploadUrl, { method: 'PUT', headers: { 'Content-Type': started.contentType }, body: file });
        if (!put.ok) throw new ProjectApiError(`Upload failed (${put.status})`, 'UPLOAD_FAILED', put.status);
        onStage?.('complete');
        const sha256 = await sha256Hex(file).catch(() => undefined);
        return this.call(`/api/projects/${encodeURIComponent(id)}/documents/${encodeURIComponent(started.document.id)}/complete`, {
            method: 'POST', body: JSON.stringify({ sha256, fileSize: file.size }),
        });
    }
}

async function sha256Hex(file: File): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ── The cue (pipeline/bim/cue.py output) ─────────────────────────────────

export interface CueItem {
    id: string; title: string; detail?: string; skill?: string; service?: string; section?: string; route?: string; records?: string;
    phase?: string; to?: string; gate?: string; field?: string;
}
export interface CuePhase {
    id: string; title: string; lod: number; state: 'past' | 'current' | 'next' | 'later'; room: string[]; entry?: string | null; status: string;
    gate: { tier: string; title: string | null; status: string | null; passed_on: string | null } | null; evidence_owed: string[];
}
export interface CueFamily { id: string; title: string; level: string | null; status?: string; reason?: string }
export interface CueGroup { id: string; title: string; domain: string; phase: string; phase_label: string; level: string | null; reason?: string; families: CueFamily[] }
export interface CueSheet { id: string; number: string; title: string; min_lod: number; profiles: string[]; eligible: boolean; ready: boolean; requires: string[] }
export interface Cue {
    phase: string; phase_label: string; lod_current: number | null; status: ProjectStatus; bod_status: BodStatus; summary: string;
    now: CueItem[]; next: CueItem[]; blocked: { title: string; reason: string }[]; evidence_owed: string[];
    phases: CuePhase[]; systems_now: CueGroup[]; look_ahead: CueGroup[]; deliverables_ready: CueSheet[];
}

/**
 * The current container, shared by the Navigator and the Info dock. Loads once
 * per container id, refreshes after every event it appends, and announces each
 * change on PROJECT_EVENT. The ledger and the cue are read here, never written.
 */
export const projectStore = {
    api: new ProjectApi(),
    project: null as ProjectRecord | null,
    events: [] as ProjectEvent[],
    documents: [] as ProjectDocument[],
    loading: false,
    error: null as string | null,
    /** Which signed-in user the cached state belongs to; a different user clears it. */
    userId: null as string | null,
    /** Bumped on every selection or user change; a load that finishes under an older generation is discarded. */
    generation: 0,

    get cue(): Cue | null { return (this.project?.cue as unknown as Cue | null) ?? null; },
    get ledger(): Ledger { return this.project?.ledger ?? {}; },

    clear(): void {
        this.project = null; this.events = []; this.documents = []; this.error = null; this.loading = false;
    },

    async load(force = false): Promise<ProjectRecord | null> {
        const id = currentProjectId();
        const gen = ++this.generation;
        const stale = () => gen !== this.generation || currentProjectId() !== id;
        if (!id) { if (this.project || this.error) { this.clear(); this.announce(); } return null; }
        if (this.project && this.project.id !== id) { this.clear(); this.announce(); }   // never show A while B loads
        if (!force && this.project?.id === id) return this.project;
        if (!this.api.isSignedIn()) { this.clear(); this.error = 'Sign in to open the project'; this.announce(); return null; }
        this.userId = this.api.userId();
        this.loading = true; this.error = null; this.announce();
        try {
            let project = await this.api.get(id);
            if (stale()) return null;
            // A recent window of the memory, from the ledger's own sequence, plus the documents.
            const after = Math.max(0, (project.ledger?.last_seq ?? 0) - 200);
            const [events, documents] = await Promise.all([this.api.events(id, after, 500), this.api.documents(id)]);
            if (stale()) return null;
            if (!project.cue || !Object.keys(project.cue).length) {
                const fresh = await this.api.cue(id, true);
                if (stale()) return null;
                project = { ...project, ledger: fresh.ledger, cue: fresh.cue };
            }
            this.project = project; this.events = events; this.documents = documents; this.error = null;
        } catch (err) {
            if (stale()) return null;
            this.clear();
            this.error = err instanceof ProjectApiError ? err.message : (err as Error).message;
            if (err instanceof ProjectApiError && err.status === 404 && currentProjectId() === id) setCurrentProjectId(null);
        } finally {
            if (!stale()) { this.loading = false; this.announce(); }
        }
        return this.project;
    },

    async refresh(): Promise<ProjectRecord | null> { return this.load(true); },

    /** The container's setup changed in the setup panel: persist it (recorded as setup.updated) and refresh. */
    async updateSetup(setup: ProjectSetup): Promise<Ledger> {
        if (!this.project) throw new ProjectApiError('No project is open', 'NO_PROJECT', 0);
        const updated = await this.api.patch(this.project.id, { setup });
        await this.refresh();
        return updated.ledger;
    },

    /** Append an event on the current container and refresh. Throws ProjectApiError (409 when the fold refuses it). */
    async append(kind: string, payload: Record<string, unknown>): Promise<Ledger> {
        if (!this.project) throw new ProjectApiError('No project is open', 'NO_PROJECT', 0);
        const { ledger } = await this.api.appendEvent(this.project.id, kind, payload);
        await this.refresh();
        return ledger;
    },

    /** The person confirms the captured basis of design; the project enters Programming. */
    async confirmBasis(): Promise<Ledger> {
        const bod = this.ledger.basis_of_design;
        if (!bod) throw new ProjectApiError('Nothing captured yet', 'NO_BASIS', 0);
        return this.append('bod.confirmed', { basis_of_design: bod, confirmed_by: this.api.userEmail() });
    },

    async advance(to: string): Promise<Ledger> { return this.append('phase.advanced', { to }); },

    /** The caller's models, flagged by whether they sit on this container. */
    async models(): Promise<LinkableModel[]> {
        if (!this.project) return [];
        return (await this.api.call<{ models: LinkableModel[] }>(`/api/projects/${encodeURIComponent(this.project.id)}/models`)).models;
    },

    /** Put a model on the container (records model.registered). */
    async linkModel(modelId: string): Promise<Ledger> {
        if (!this.project) throw new ProjectApiError('No project is open', 'NO_PROJECT', 0);
        const out = await this.api.call<{ ledger: Ledger }>(`/api/projects/${encodeURIComponent(this.project.id)}/models/${encodeURIComponent(modelId)}/link`, { method: 'POST', body: '{}' });
        await this.refresh();
        return out.ledger;
    },

    /** Issue the draft permit set from the container's model; records deliverable.issued and a Tier B gate.run. */
    async issuePermitSet(modelId?: string): Promise<IssuedSet> {
        if (!this.project) throw new ProjectApiError('No project is open', 'NO_PROJECT', 0);
        const out = await this.api.call<IssuedSet>(`/api/projects/${encodeURIComponent(this.project.id)}/deliverables/permit-set`, { method: 'POST', body: JSON.stringify(modelId ? { modelId } : {}) });
        await this.refresh();
        return out;
    },

    /** The contract tiers with their state here, the built contracts, and the network's registered ones. */
    /** The supplier records the DD steps can resolve to. */
    async ddSuppliers(): Promise<DdSupplier[]> {
        if (!this.project) throw new ProjectApiError('No project open', 'NO_PROJECT', 409);
        return (await this.api.call<{ suppliers: DdSupplier[] }>(`/api/projects/${encodeURIComponent(this.project.id)}/dd/suppliers`)).suppliers;
    },

    /** Run a DD step on the container's latest model: a new model version is linked and the DD event recorded. */
    async ddRun(step: DdStep, body: Record<string, unknown> = {}): Promise<DdResult> {
        if (!this.project) throw new ProjectApiError('No project open', 'NO_PROJECT', 409);
        const out = await this.api.call<DdResult>(`/api/projects/${encodeURIComponent(this.project.id)}/dd/${step}`, { method: 'POST', body: JSON.stringify(body) });
        await this.refresh();
        return out;
    },

    /** The DD quantities for the GMP as a package deliverable on the latest model (or modelId). */
    async issueQuantities(modelId?: string): Promise<QuantitiesIssue> {
        if (!this.project) throw new ProjectApiError('No project open', 'NO_PROJECT', 409);
        const out = await this.api.call<QuantitiesIssue>(`/api/projects/${encodeURIComponent(this.project.id)}/deliverables/quantities`, { method: 'POST', body: JSON.stringify(modelId ? { modelId } : {}) });
        await this.refresh();
        return out;
    },

    async contracts(): Promise<ContractsView> {
        if (!this.project) throw new ProjectApiError('No project is open', 'NO_PROJECT', 0);
        return this.api.call<ContractsView>(`/api/projects/${encodeURIComponent(this.project.id)}/contracts`);
    },

    /** Build a tier's contract from the lab's sources; records contract.generated. */
    async buildContract(tier: string): Promise<Ledger> {
        if (!this.project) throw new ProjectApiError('No project is open', 'NO_PROJECT', 0);
        const out = await this.api.call<{ ledger: Ledger }>(`/api/projects/${encodeURIComponent(this.project.id)}/contracts/build`, { method: 'POST', body: JSON.stringify({ tier }) });
        await this.refresh();
        return out.ledger;
    },

    /** Check the container's model against a built contract; records gate.run. */
    async checkContract(documentId: string, modelId?: string): Promise<ContractCheck> {
        if (!this.project) throw new ProjectApiError('No project is open', 'NO_PROJECT', 0);
        const out = await this.api.call<ContractCheck>(`/api/projects/${encodeURIComponent(this.project.id)}/contracts/${encodeURIComponent(documentId)}/check`, { method: 'POST', body: JSON.stringify(modelId ? { modelId } : {}) });
        await this.refresh();
        return out;
    },

    /** The deliberate step onto the network's registry (501 until the contracts.register seam is built). */
    async registerContract(documentId: string): Promise<void> {
        if (!this.project) throw new ProjectApiError('No project is open', 'NO_PROJECT', 0);
        await this.api.call(`/api/projects/${encodeURIComponent(this.project.id)}/contracts/${encodeURIComponent(documentId)}/register`, { method: 'POST', body: '{}' });
        await this.refresh();
    },

    /** Read a scheme document's text and deterministic hits (worker, no model). */
    async readDocumentText(documentId: string): Promise<{ pageCount: number; hasText: boolean; hits: Candidate[] }> {
        if (!this.project) throw new ProjectApiError('No project is open', 'NO_PROJECT', 0);
        const token = await this.api['auth'].getToken();
        const res = await fetch(`/api/projects/${encodeURIComponent(this.project.id)}/documents/${encodeURIComponent(documentId)}/text`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new ProjectApiError(body?.error?.message || `${res.status}`, body?.error?.code || 'HTTP_ERROR', res.status);
        await this.refresh();
        return body;
    },

    /** Record an extraction (the agent's or the worker's candidates) as bod.extracted. */
    async recordExtraction(payload: { document_id?: string; candidates: Candidate[]; extracted_by?: string; questions?: string[] }): Promise<Ledger> {
        return this.append('bod.extracted', { document_id: payload.document_id ?? this.documents.find(d => d.kind === 'scheme')?.id ?? 'none', candidates: payload.candidates, extracted_by: payload.extracted_by ?? 'person', questions: payload.questions ?? [] });
    },

    /** Merge accepted candidates into the captured basis and re-record it as captured (mirror of basis_of_design.apply_candidates). */
    async applyCandidates(candidates: Candidate[]): Promise<Ledger> {
        const bod = JSON.parse(JSON.stringify(this.ledger.basis_of_design ?? {})) as BasisOfDesign;
        for (const c of candidates) {
            const v = c.value as unknown;
            if (['community_type', 'occupancy_group', 'construction_type', 'budget_tier', 'domain', 'intent', 'project_name'].includes(c.field)) {
                if (v != null && v !== '') (bod as unknown as Record<string, unknown>)[c.field] = v;
            } else if (c.field === 'program.spaces') {
                const names = Array.isArray(v) ? v : [v];
                const spaces = [...(bod.program?.spaces ?? [])];
                const have = new Set(spaces.map(s => s.name.toLowerCase()));
                for (const n of names) {
                    const entry = typeof n === 'object' && n ? n as ProgramSpace : { name: String(n) };
                    if (!have.has(entry.name.toLowerCase())) { spaces.push(entry); have.add(entry.name.toLowerCase()); }
                }
                bod.program = { ...(bod.program ?? {}), spaces };
            } else if (c.field === 'systems_requested') {
                const vals = (Array.isArray(v) ? v : [v]).map(String);
                bod.systems_requested = [...new Set([...(bod.systems_requested ?? []), ...vals])];
            } else if (c.field === 'performance.targets') {
                const vals = (Array.isArray(v) ? v : [v]).map(String);
                bod.performance = { targets: [...(bod.performance?.targets ?? []), ...vals] };
            }
        }
        return this.append('bod.captured', { basis_of_design: bod });
    },

    announce(): void { window.dispatchEvent(new CustomEvent(PROJECT_EVENT, { detail: { projectId: this.project?.id ?? null, loaded: true } })); },
};

// A different signed-in user (or a sign-out) invalidates the cached container and any load in flight.
projectStore.api.onAuthChange((state) => {
    const uid = state.user?.id ?? null;
    if (uid === projectStore.userId && (uid || !projectStore.project)) return;
    projectStore.userId = uid;
    projectStore.generation++;
    projectStore.clear();
    projectStore.announce();
    if (uid && currentProjectId()) void projectStore.load(true);
});

/** The open container's setup when there is one, else the viewer's local draft. One source for every reader. */
export function activeSetup(): ProjectSetup {
    const s = projectStore.project?.setup as ProjectSetup | undefined;
    return s && Array.isArray(s.structures) ? s : loadSetup();
}

/** Human labels for the phases the ledger reports: the pattern's titles (one spine); setup is the container's own pre-phase. */
export const PHASE_LABEL: Record<string, string> = {
    setup: 'Setup',
    ...Object.fromEntries((designBuildPattern.phases as { id: string; title: string }[]).map(p => [p.id, p.title])),
};

export function phaseLabel(phase: string | undefined | null): string {
    return phase ? (PHASE_LABEL[phase] ?? phase) : 'Setup';
}
